import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import * as DM from '@radix-ui/react-dropdown-menu';
import cn from 'classnames';
import { DropdownMenuOption } from '@repo/utils/types';
import { ExpandingMenu } from '@repo/components/menus';
import styles from './Menu.module.scss';
import { useMountedEffect, useIsOnScreen } from '@repo/hooks';
import { EllipsisToXIcon } from '@repo/icons/EllipsisToXIcon';

// TODO: use the new trigger in browser extension component
// TODO: will also want to create a trigger for "more" menu
import {
  ContactOptionsTrigger,
  BrowserExtensionContactOptionsTrigger,
} from './triggers/ContactOptionsTrigger';
import { BrowserExtensionMoreOptionsTrigger } from './triggers/BrowserExtensionMoreOptionsTrigger';
import { UdoTrigger } from './triggers/UdoTrigger';
import { IntegrationActionsTrigger } from './triggers/IntegrationActionsTrigger';
import { ResourcePermissionsTrigger } from './triggers/ResourcePermissionsTrigger';
import { EllipsisIcon } from '@repo/icons';

function FallbackTrigger({ isOpen, triggerProps }) {
  if (!!triggerProps.children) {
    return triggerProps.children;
  }

  return <EllipsisToXIcon isOpen={isOpen} triggerProps={triggerProps} />;
}

function StaticEllipsisTrigger({ isOpen, triggerProps }) {
  return (
    <EllipsisIcon {...triggerProps} className={cn(triggerProps.className, isOpen && 'menuOpen')} />
  );
}

export type DropdownTriggerVariant =
  | 'udo'
  | 'permissions'
  | 'contactOptions'
  | 'integrationActions'
  | 'browserExtensionContactOptions'
  | 'browserExtensionMoreOptions'
  | 'staticEllipsis';

export type DropdownExpandFromOrigin =
  | 'top-right'
  | 'bottom-left'
  | 'top-center'
  | 'top-left'
  | 'bottom-right';

interface DropdownMenuProps {
  options: Array<DropdownMenuOption>;
  before?: ReactNode;
  menuClassName?: string;
  triggerClassName?: string;
  triggerProps?: Record<string, any>;
  expandFrom?: DropdownExpandFromOrigin;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  triggerVariant?: DropdownTriggerVariant;
  side?: 'bottom' | 'top';
  suppressFocusControlOnItems?: boolean;
  align?: 'end' | 'start' | 'center';
}

// This allows us to pass the isOpen prop (and any arbitrary props passed as triggerProps) to the trigger
// component. Alternatively any trigger component can be passed as a ReactNode as the triggerProps.children.
// We might want to come back to this at some point to standardize the trigger component API.
const triggerForMenu: Record<DropdownTriggerVariant, (props: any) => JSX.Element> = {
  udo: UdoTrigger,
  permissions: ResourcePermissionsTrigger,
  contactOptions: ContactOptionsTrigger,
  integrationActions: IntegrationActionsTrigger,
  browserExtensionContactOptions: BrowserExtensionContactOptionsTrigger,
  browserExtensionMoreOptions: BrowserExtensionMoreOptionsTrigger,
  staticEllipsis: StaticEllipsisTrigger,
};

export function DropdownMenu({
  options,
  before = null,
  menuClassName = null,
  triggerClassName = null,
  triggerProps = {},
  expandFrom,
  triggerVariant,
  onOpen,
  onClose,
  disabled,
  side,
  suppressFocusControlOnItems = false,
  align,
}: DropdownMenuProps) {
  const isMounted = useRef(true);
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const Trigger = useMemo(
    () => (triggerVariant ? triggerForMenu[triggerVariant] : FallbackTrigger),
    [triggerVariant],
  );
  const shouldOpenBelow = useIsOnScreen(triggerRef, { rootMargin: '0px 0px -220px 0px' });

  let _expandFrom = expandFrom;
  let _side: 'bottom' | 'top' = 'bottom';

  if (!expandFrom && !side) {
    _expandFrom = 'top-right';
    _side = 'bottom';

    if (!shouldOpenBelow) {
      _expandFrom = 'bottom-right';
      _side = 'top';
    }
  }

  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
  }, [isOpen]);

  useMountedEffect(() => {
    if (!isOpen && onClose) {
      onClose();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const onOpenChange = (open: boolean) => {
    if (isMounted.current) {
      setIsOpen(open);
    }
  };

  return (
    <DM.Root open={isOpen} onOpenChange={onOpenChange}>
      <DM.Trigger
        className={cn(
          triggerClassName,
          styles.toggle,
          !triggerVariant && !triggerProps?.children && styles.ellipsisTriggerContainer,
          triggerVariant === 'browserExtensionContactOptions' && 'extension-profile-action-btn',
        )}
        asChild
        disabled={disabled}
        ref={triggerRef}
      >
        {/* this is wrapped for now so that ref and classname can be properly applied by radix, should figure out how to unwrap! */}
        <div>
          <Trigger isOpen={isOpen} triggerProps={triggerProps} />
        </div>
      </DM.Trigger>
      <ExpandingMenu
        options={options}
        isOpen={isOpen}
        menuClassName={menuClassName}
        expandFrom={_expandFrom}
        before={before}
        side={_side}
        suppressFocusControlOnItems={suppressFocusControlOnItems}
        align={align}
      />
    </DM.Root>
  );
}
